import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
  findOtherValue,
  flattenPreferredTimes,
  preferredDaysInWorkOrder,
  PreferredServiceDay,
  removeDayEntries,
  removeOtherEntry,
  toggleEntry,
  withOtherEntry,
} from './timeSlotHelpers';
import { timeSlotsForTheme, workDaysForTheme } from './timeSlotOptions';

const buildInitialSelection = (
  fromScheduling?: PreferredServiceDay[]
): string[] => {
  if (!Array.isArray(fromScheduling) || fromScheduling.length === 0) {
    return [];
  }

  return flattenPreferredTimes(fromScheduling);
};

interface UseTimeSlotSelectionParams {
  fromScheduling?: PreferredServiceDay[];
  from?: string;
  onSelectionChange: (selection: string) => void;
}

export interface UseTimeSlotSelectionReturn {
  tab: string;
  selection: string[];
  filterTab: string | null;
  filterDay: string[];
  otherValue: string | null;
  otherSelected: boolean;

  slots: string[];
  days: string[];
  isSchedulingView: boolean;
  showSchedulingError: boolean;

  isSlotSelected: (slotLabel: string) => boolean;

  onDaySelect: (day: string) => void;
  onFilterTabSelect: (day: string) => void;
  onSlotToggle: (slotLabel: string) => void;
  onOtherToggle: () => void;
  onOtherValueChange: (value: string) => void;
}

interface TimeSlotSelectionState {
  tab: string;
  selection: string[];
  filterTab: string | null;
  filterDay: string[];
  otherValue: string | null;
  otherSelected: boolean;
}

const TOAST_OPTIONS = {
  autoClose: 12000,
  position: 'top-center' as const,
};

export const useTimeSlotSelection = ({
  fromScheduling,
  from,
  onSelectionChange,
}: UseTimeSlotSelectionParams): UseTimeSlotSelectionReturn => {
  const [state, setState] = useState<TimeSlotSelectionState>({
    tab: 'Monday',
    selection: buildInitialSelection(fromScheduling),
    filterTab: null,
    filterDay: [],
    otherValue: null,
    otherSelected: false,
  });

  const theme: string | null = localStorage.getItem('theme');

  const slots: string[] = timeSlotsForTheme(theme);
  const days: string[] = workDaysForTheme(theme);

  const isSchedulingView: boolean =
    from === 'srscheduling' && theme !== 'Rize' && theme !== 'Formwerks';

  const hasSchedulingData: boolean =
    Array.isArray(fromScheduling) && fromScheduling.length > 0;

  // A bad/empty preferredServiceTimes payload leaves nothing to schedule against —
  // show a graceful message rather than an empty (or crashed) picker.
  const showSchedulingError: boolean = isSchedulingView && !hasSchedulingData;

  const applySelection = (nextSelection: string[]) => {
    const commaSeparatedSelection: string = nextSelection.join(',');

    onSelectionChange(commaSeparatedSelection);
  };

  const initializeSchedulingView = () => {
    if (hasSchedulingData) {
      applySelection(state.selection);
    }

    if (!isSchedulingView) {
      return;
    }

    /* Guard: some tenants (e.g. Wesgroup) send an empty preferredServiceTimes list.
     * Bail before reading fromScheduling[0] so we render the error state
     * instead of crashing on undefined. Root cause is upstream (the API shouldn't
     * return an empty list here) — warn so it's observable, not silently swallowed. */
    if (!fromScheduling || fromScheduling.length === 0) {
      console.warn(
        'TimeSlot: srscheduling view received an empty preferredServiceTimes list; rendering error state.'
      );

      return;
    }

    const preferredDays: string[] = preferredDaysInWorkOrder(
      days,
      fromScheduling
    );

    setState((prev: TimeSlotSelectionState) => ({
      ...prev,
      filterTab: fromScheduling[0].day,
      filterDay: preferredDays,
    }));
  };

  useEffect(() => {
    initializeSchedulingView();
  }, []);

  const isSlotSelected = (slotLabel: string): boolean => {
    const entry: string = `${state.tab} ${slotLabel}`;

    return state.selection.includes(entry);
  };

  const onSlotToggle = (slotLabel: string) => {
    const entry: string = `${state.tab} ${slotLabel}`;

    const withoutOther: string[] = removeOtherEntry(state.selection, state.tab);
    const nextSelection: string[] = toggleEntry(withoutOther, entry);

    setState((prev: TimeSlotSelectionState) => ({
      ...prev,
      otherSelected: false,
      otherValue: '',
      selection: nextSelection,
    }));

    applySelection(nextSelection);
  };

  const onOtherToggle = () => {
    const nextSelection: string[] = removeDayEntries(state.selection, state.tab);

    setState((prev: TimeSlotSelectionState) => ({
      ...prev,
      otherSelected: true,
      otherValue: null,
      selection: nextSelection,
    }));

    applySelection(nextSelection);
  };

  const onOtherValueChange = (value: string) => {
    const nextSelection: string[] = withOtherEntry(
      state.selection,
      state.tab,
      value
    );

    setState((prev: TimeSlotSelectionState) => ({
      ...prev,
      otherSelected: true,
      otherValue: value,
      selection: nextSelection,
    }));

    applySelection(nextSelection);
  };

  // legacy/workaround: restores the saved Other text after the tab switch renders;
  // the 500ms delay predates this refactor and is kept to preserve behaviour.
  const restoreOtherValueAfterTabSwitch = (value: string) => {
    setTimeout(() => {
      setState((prev: TimeSlotSelectionState) => ({
        ...prev,
        otherSelected: true,
        otherValue: value,
      }));
    }, 500);
  };

  const onDaySelect = (day: string) => {
    const targetDayOtherValue: string | null = findOtherValue(
      state.selection,
      day
    );

    if (targetDayOtherValue !== null) {
      restoreOtherValueAfterTabSwitch(targetDayOtherValue);
    }

    const otherNeedsValue: boolean =
      state.otherSelected && !state.otherValue;

    if (otherNeedsValue) {
      toast.error(' 🚫 Other field is required.', TOAST_OPTIONS);

      return;
    }

    if (state.otherSelected) {
      const nextSelection: string[] = withOtherEntry(
        state.selection,
        state.tab,
        state.otherValue as string
      );

      setState((prev: TimeSlotSelectionState) => ({
        ...prev,
        otherSelected: false,
        tab: day,
        selection: nextSelection,
        otherValue: null,
      }));

      applySelection(nextSelection);

      return;
    }

    setState((prev: TimeSlotSelectionState) => ({ ...prev, tab: day }));
  };

  const onFilterTabSelect = (day: string) => {
    setState((prev: TimeSlotSelectionState) => ({ ...prev, filterTab: day }));
  };

  return {
    tab: state.tab,
    selection: state.selection,
    filterTab: state.filterTab,
    filterDay: state.filterDay,
    otherValue: state.otherValue,
    otherSelected: state.otherSelected,

    slots,
    days,
    isSchedulingView,
    showSchedulingError,

    isSlotSelected,

    onDaySelect,
    onFilterTabSelect,
    onSlotToggle,
    onOtherToggle,
    onOtherValueChange,
  };
};